import {TASK_RETENTION_IN_MONTHS} from '@nursio-link/common/utils';
import dayjs from 'dayjs';
import {CloudMoon, Sun, Sunrise, Sunset} from 'lucide-react';
import {sortObjectByKeys} from 'nx/src/utils/object-sort';
import {
  ascend,
  descend,
  groupBy,
  partition,
  pathOr,
  pipe,
  sortWith,
} from 'ramda';
import type {ReactNode} from 'react';

import type {GetTasksWithDateRangeInput, Task} from '@/services/graphqlHooks';
import {PartOfTheDay} from '@/services/graphqlHooks';
import {TIME_FORMATS} from '@/utils/timeFormats';

import type {ForecastedTask} from './getForecastedTasks';

export type CombinedTask = Task & ForecastedTask;

export const getTaskLocalDateTime = (task: Task) => {
  if (!task.date || !task.completeUntil?.time) return undefined;

  return dayjs.utc(`${task.date}T${task.completeUntil.time}`).local();
};

export const getTaskLocalDate = (task: Task): string =>
  getTaskLocalDateTime(task)?.format(TIME_FORMATS.awsDate) ?? task.date!;

export const groupByDay = (task: Task) => getTaskLocalDate(task);

export const sortAnnouncementTasks = (tasks: Task[]): Task[] => {
  return sortWith([descend(pathOr('', ['createdAt']))], tasks);
};

const PART_OF_DAY_DISPLAY_ORDER = [
  PartOfTheDay.Morning,
  PartOfTheDay.Afternoon,
  PartOfTheDay.Evening,
];

const MORNING_START_HOUR = 6;

const groupByPartOfTheDay = (task: Task) =>
  task.completeUntil?.partOfTheDay ?? 'none';

const sortByHourThenTitle = sortWith([
  ascend((task: Task) => (task.completeUntil?.time ? 0 : 1)),
  ascend((task: Task) => getTaskLocalDateTime(task)?.valueOf() ?? 0),
  ascend(pathOr('', ['title'])),
]);

const isNightTaskBeforeDayStart = (task: Task) =>
  (getTaskLocalDateTime(task)?.hour() ?? 0) < MORNING_START_HOUR;

export const sortTasks = (tasks: Task[]): Task[] => {
  const groupedTasksPerDayMap = pipe(
    groupBy(groupByDay),
    sortObjectByKeys
  )(tasks);
  const days = Object.values(groupedTasksPerDayMap);

  return days.flatMap(e => {
    const tasksByPartOfDay = groupBy(groupByPartOfTheDay, e as Task[]);
    const nightTasks = tasksByPartOfDay[PartOfTheDay.Night] ?? [];
    const [timedNightTasks, noTimeNightTasks] = partition(
      (task: Task) => Boolean(task.completeUntil?.time),
      nightTasks
    );
    const [nightTasksBeforeDayStart, nightTasksAfterDayEnd] = partition(
      isNightTaskBeforeDayStart,
      timedNightTasks
    );

    return [
      ...sortByHourThenTitle(tasksByPartOfDay['none'] ?? []),
      ...sortByHourThenTitle(nightTasksBeforeDayStart),
      ...PART_OF_DAY_DISPLAY_ORDER.flatMap(partOfTheDay =>
        sortByHourThenTitle(tasksByPartOfDay[partOfTheDay] ?? [])
      ),
      ...sortByHourThenTitle(nightTasksAfterDayEnd),
      ...sortByHourThenTitle(noTimeNightTasks),
    ];
  });
};

const LOCAL_TO_UTC_DATE_MARGIN_IN_DAYS = 1;

export const tasksWithDateRangeInput = (input?: GetTasksWithDateRangeInput) => {
  const startDate = dayjs(input?.startDate).isValid()
    ? input?.startDate
    : undefined;
  const endDate = dayjs(input?.endDate).isValid() ? input?.endDate : undefined;

  return {
    startDate: dayjs(startDate)
      .subtract(LOCAL_TO_UTC_DATE_MARGIN_IN_DAYS, 'day')
      .format(TIME_FORMATS.awsDate),
    endDate: dayjs(endDate)
      .add(LOCAL_TO_UTC_DATE_MARGIN_IN_DAYS, 'day')
      .format(TIME_FORMATS.awsDate),
  };
};

export const getPartOfTheDayIcon = (
  partOfTheDay: PartOfTheDay,
  size?: number
): ReactNode => {
  switch (partOfTheDay) {
    case PartOfTheDay.Morning:
      return <Sunrise size={size} />;
    case PartOfTheDay.Afternoon:
      return <Sun size={size} />;
    case PartOfTheDay.Evening:
      return <Sunset size={size} />;
    case PartOfTheDay.Night:
      return <CloudMoon size={size} />;
    default:
      return null;
  }
};

interface PartOfTheDayClasses {
  wrapper: string;
  indicator: string;
  icon: string;
  text: string;
}

export const getPartOfTheDayClasses = (
  partOfTheDay: PartOfTheDay
): PartOfTheDayClasses => {
  switch (partOfTheDay) {
    case PartOfTheDay.Morning:
      return {
        wrapper: 'border-blue-500',
        indicator: 'bg-blue-500',
        icon: 'text-blue-500',
        text: 'text-blue-700',
      };
    case PartOfTheDay.Afternoon:
      return {
        wrapper: 'border-yellow-500',
        indicator: 'bg-yellow-500',
        icon: 'text-yellow-500',
        text: 'text-yellow-600',
      };
    case PartOfTheDay.Evening:
      return {
        wrapper: 'border-orange-500',
        indicator: 'bg-orange-500',
        icon: 'text-orange-500',
        text: 'text-orange-700',
      };
    case PartOfTheDay.Night:
      return {
        wrapper: 'border-purple-500',
        indicator: 'bg-purple-500',
        icon: 'text-purple-500',
        text: 'text-purple-700',
      };
    default:
      return {
        wrapper: 'border-neutral',
        indicator: 'bg-neutral',
        icon: 'text-neutral-dark',
        text: 'text-neutral-dark',
      };
  }
};

export const getIsOverdue = (time?: string) => {
  if (!time) return;

  return dayjs.utc().isAfter(dayjs.utc(time));
};

export const isArchivedTaskPeriod = (endDate: string): boolean =>
  dayjs(endDate).isBefore(
    dayjs().subtract(TASK_RETENTION_IN_MONTHS, 'month'),
    'day'
  );

export const isStandardTask = (task: Task) =>
  Boolean(task?.completeUntil?.partOfTheDay || task?.completeUntil?.time);

export const splitTasksByType = (tasks: Task[]) => {
  return {
    announcementTasks: tasks.filter(task => !isStandardTask(task)),
    standardTasks: tasks.filter(isStandardTask),
  };
};
